import { motion } from 'framer-motion';
import { 
  Zap, 
  TrendingDown, 
  Target, 
  Handshake, 
  RefreshCw, 
  BarChart3,
  Newspaper,
  ExternalLink,
  Flame,
  Clock,
  ClipboardCheck,
  HeartHandshake,
  Hammer,
  Lightbulb
} from 'lucide-react';
import whyUsImage from '@/assets/why-us-hurco.png';
import zeitungsartikelImage from '@/assets/zeitungsartikel-landrat.jpg';

const WhyUsSection = () => {
  const benefits = [
    {
      icon: Zap,
      title: 'Geschwindigkeit',
      description: 'Keine Wartezeiten, sofortige Bearbeitung Ihrer Anfrage',
      color: 'fastlane',
    },
    {
      icon: TrendingDown,
      title: 'Wirtschaftlichkeit',
      description: 'Skalierbare Dienstleistung ohne Kapitalbindung',
      color: 'smart',
    },
    {
      icon: Target,
      title: 'Qualitätskontrolle',
      description: 'Fertigungsbegleitende Prüfungen mit modernsten Messmitteln',
      color: 'primary',
    },
    {
      icon: Handshake,
      title: 'Partnerschaft',
      description: 'Direkter Draht zu Experten, keine Hierarchien',
      color: 'primary',
    },
    {
      icon: RefreshCw,
      title: 'Flexibilität',
      description: 'Kurzfristige Änderungen jederzeit möglich',
      color: 'fastlane',
    },
    {
      icon: BarChart3,
      title: 'Transparenz',
      description: 'Maximale Rückverfolgbarkeit durch Fotodokumentation',
      color: 'smart',
    },
  ];

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'fastlane':
        return {
          bg: 'bg-fastlane/10 group-hover:bg-fastlane/20',
          text: 'text-fastlane',
          glow: 'group-hover:shadow-fastlane',
        };
      case 'smart':
        return {
          bg: 'bg-smart/10 group-hover:bg-smart/20',
          text: 'text-smart',
          glow: 'group-hover:shadow-smart',
        };
      default:
        return {
          bg: 'bg-primary/10 group-hover:bg-primary/20',
          text: 'text-primary',
          glow: 'group-hover:shadow-lg',
        };
    }
  };

  return (
    <section id="warum-wir" className="py-20 lg:py-32 bg-gradient-steel pattern-grid">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-4">
            Warum SR-Zerspanung?
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Ihre Vorteile bei der Zusammenarbeit mit uns
          </p>
        </motion.div>

        {/* Two Column Layout: Image + Benefits */}
        <div className="grid lg:grid-cols-2 gap-12 max-w-7xl mx-auto items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/3]">
              <img 
                loading="lazy"
                decoding="async"
                src={whyUsImage} 
                alt="SR-Zerspanung Mitarbeiter an HURCO CNC-Maschine" 
                className="w-full h-full object-cover object-[70%_center]"
              />
              {/* Subtle overlay for depth */}
              <div className="absolute inset-0 bg-gradient-to-t from-primary/20 via-transparent to-transparent" />
            </div>
            {/* Decorative element */}
            <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-fastlane/20 rounded-2xl -z-10" />
            <div className="absolute -top-4 -left-4 w-16 h-16 bg-smart/20 rounded-xl -z-10" />
          </motion.div>

          {/* Benefits Grid */}
          <div className="grid sm:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              const colors = getColorClasses(benefit.color);

              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`group bg-card rounded-xl p-5 border border-border hover:border-transparent transition-all duration-300 ${colors.glow}`}
                >
                  <div className={`w-10 h-10 rounded-lg ${colors.bg} flex items-center justify-center mb-3 transition-colors`}>
                    <Icon className={`w-5 h-5 ${colors.text}`} />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-foreground mb-1">
                    {benefit.title}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {benefit.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Uns zeichnet aus */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-20 max-w-5xl mx-auto"
        >
          <h3 className="font-heading font-bold text-2xl md:text-3xl text-foreground text-center mb-10">
            Was uns auszeichnet
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: 'Leidenschaft zum Beruf', description: 'Vollblut-Zerspaner mit Herzblut – keine Knöpfchendrücker.' },
              { title: 'Höchste Zuverlässigkeit', description: 'Unsere Liefertermine sind garantiert.' },
              { title: 'Perfekt organisiert', description: 'Wir überlassen nichts dem Zufall.' },
              { title: 'Immer für Sie da', description: 'Wenn Sie uns brauchen, sind wir zur Stelle.' },
              { title: 'Hands-on-Mentalität', description: 'Wir packen an und liefern Ergebnisse.' },
              { title: 'Kreative Problemlöser', description: 'Wo andere aufgeben, finden wir eine Lösung.' },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.07 }}
                className="rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow border border-white/20"
              >
                <div className="bg-primary px-5 py-2.5">
                  <h4 className="font-heading font-bold text-sm text-primary-foreground">
                    {item.title}
                  </h4>
                </div>
                <div className="backdrop-blur-xl bg-white/30 border-t border-white/40 px-5 py-4 shadow-[inset_0_1px_1px_rgba(255,255,255,0.4)]">
                  <p className="text-foreground/80 text-sm">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Press Feature */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 max-w-4xl mx-auto"
        >
          <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
            <div className="grid md:grid-cols-2 gap-0">
              {/* Image */}
              <div className="relative aspect-[4/3] md:aspect-auto">
                <img 
                  loading="lazy"
                  decoding="async"
                  src={zeitungsartikelImage} 
                  alt="Landrat Franz Löffler besucht SR-Zerspanung" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4 bg-primary text-white px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2">
                  <Newspaper className="w-4 h-4" />
                  Chamer Zeitung
                </div>
              </div>
              
              {/* Content */}
              <div className="p-6 md:p-8 flex flex-col justify-center">
                <h3 className="font-heading font-bold text-xl md:text-2xl text-foreground mb-3">
                  Anerkennung für Unternehmer aus der Region
                </h3>
                <p className="text-muted-foreground mb-4">
                  Landrat Franz Löffler würdigt den Mut und Fleiß von Inhaber Thomas Schindler beim Besuch der Firma SR-Zerspanung in Stamsried – seit 2014 ein verlässlicher Partner in der Zerspanungstechnik.
                </p>
                <blockquote className="border-l-4 border-primary pl-4 italic text-muted-foreground text-sm mb-6">
                  „Toll, solche jungen Leute, die unternehmerisch tätig sind, brauchen wir!"
                  <span className="block mt-1 not-italic font-medium text-foreground">– Landrat Franz Löffler</span>
                </blockquote>
                <a 
                  href="/presse/Chamer_Zeitung_West_Anerkennung_fuer_Jungunternehmer.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Artikel lesen
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyUsSection;
